import { useEffect, useState } from "react";
import { Modal } from "./Modal";
import { TRACKS, INTAKE_FIELDS } from "../../data/content";
import { api, API, formatApiErrorDetail } from "../../lib/api";
import { Loader2, Paperclip, ShieldCheck, Copy, Check } from "lucide-react";
import axios from "axios";

const inputCls =
  "w-full px-4 py-3 rounded-xl border border-[#E2E8F0] bg-white text-[#0B1B2B] text-sm focus:outline-none focus:border-[#D4AF37] focus:ring-1 focus:ring-[#D4AF37] transition-colors";

const VaultBadge = () => (
  <div className="inline-flex items-center gap-3 px-5 py-3 rounded-xl border border-[#D4AF37]/50 bg-[#0B1B2B]" data-testid="vault-badge">
    <svg width="30" height="34" viewBox="0 0 30 34" aria-hidden="true">
      <path d="M15 1 L28 6 V16 C28 25 22 31 15 33 C8 31 2 25 2 16 V6 Z" fill="none" stroke="#D4AF37" strokeWidth="1.6" />
      <rect x="10" y="14" width="10" height="9" rx="1.5" fill="#D4AF37" />
      <path d="M12 14 v-3 a3 3 0 0 1 6 0 v3" fill="none" stroke="#D4AF37" strokeWidth="1.6" />
    </svg>
    <div className="text-left">
      <div className="text-[#D4AF37] text-xs font-semibold tracking-[0.15em] uppercase">256-Bit Encrypted</div>
      <div className="text-white/80 text-xs">Secure Intake Vault</div>
    </div>
  </div>
);

export const IntakeModal = ({ open, onClose, trackId, presetSpecialty, presetValues }) => {
  const track = TRACKS.find((t) => t.id === trackId);
  const [schema, setSchema] = useState(null);
  const [values, setValues] = useState({});
  const [file, setFile] = useState(null);
  const [docMeta, setDocMeta] = useState({ document_type: "", description: "", expires_on: "" });
  const [consent, setConsent] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [result, setResult] = useState(null);
  const [copied, setCopied] = useState(false);

  const fields = schema?.fields || INTAKE_FIELDS[trackId] || [];
  const allowUpload = schema ? schema.allow_upload : trackId === "preceptors";

  useEffect(() => {
    if (open) {
      setValues({
        ...(presetValues || {}),
        ...(presetSpecialty ? { specialty: presetSpecialty, desiredSpecialty: presetSpecialty, programs: presetSpecialty } : {}),
      });
      setFile(null);
      setDocMeta({ document_type: "", description: "", expires_on: "" });
      setConsent(false);
      setResult(null);
      setError("");
      setSchema(null);
      if (trackId) {
        api.get(`/intake/forms/${trackId}`).then(({ data }) => setSchema(data)).catch(() => setSchema(null));
      }
    }
  }, [open, trackId, presetSpecialty, presetValues]);

  if (!track) return null;

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("track", trackId);
      fd.append("payload", JSON.stringify({ ...values, consent }));
      if (file) {
        fd.append("file", file);
        fd.append("file_meta", JSON.stringify(docMeta));
      }
      const { data } = await axios.post(`${API}/intake`, fd);
      setResult(data);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setBusy(false);
    }
  };

  const copyRef = () => {
    navigator.clipboard.writeText(result.reference_id);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };

  return (
    <Modal open={open} onClose={onClose} wide testId="intake-modal">
      {result ? (
        <div className="p-8 md:p-12 text-center" data-testid="intake-confirmation">
          <div className="mx-auto w-14 h-14 rounded-full bg-[#D4AF37]/15 flex items-center justify-center">
            <ShieldCheck size={28} className="text-[#D4AF37]" />
          </div>
          <h2 className="mt-6 font-serif text-2xl md:text-3xl text-[#0B1B2B] tracking-tight">Submission Secured</h2>
          <p className="mt-3 text-[#64748B] max-w-md mx-auto leading-relaxed">
            Your materials have been received into our encrypted intake vault. A confirmation has been dispatched to your email, and our executive operations team has been notified.
          </p>
          <div className="mt-8 inline-flex items-center gap-3 px-6 py-4 bg-[#F8FAFC] border border-[#E2E8F0] rounded-xl">
            <div className="text-left">
              <div className="text-xs uppercase tracking-[0.15em] text-[#64748B]">Reference ID</div>
              <div className="font-serif text-xl font-bold text-[#0B1B2B]" data-testid="reference-id">{result.reference_id}</div>
            </div>
            <button onClick={copyRef} className="p-2 rounded-lg hover:bg-white transition-colors" data-testid="copy-reference-button" aria-label="Copy reference">
              {copied ? <Check size={16} className="text-green-600" /> : <Copy size={16} className="text-[#64748B]" />}
            </button>
          </div>
          {result.file_received && (
            <p className="mt-4 text-sm text-[#64748B]">
              Credential file <span className="font-medium text-[#0B1B2B]">{result.file_received}</span> secured in vault.
            </p>
          )}
          <div className="mt-8"><VaultBadge /></div>
          <div className="mt-8">
            <button onClick={onClose} className="bg-[#0B1B2B] text-white font-medium px-8 py-3 rounded-xl hover:bg-[#152a40] transition-colors" data-testid="confirmation-done-button">
              Done
            </button>
          </div>
        </div>
      ) : (
        <div className="p-8 md:p-10">
          <p className="text-xs tracking-[0.2em] uppercase text-[#D4AF37] font-semibold">{track.overline} · Intake</p>
          <h2 className="mt-2 font-serif text-2xl md:text-3xl text-[#0B1B2B] tracking-tight">{track.headline}</h2>
          <p className="mt-3 text-sm text-[#64748B]">All submissions are transmitted to our 256-bit encrypted intake vault.</p>
          <form onSubmit={submit} className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-4">
            {fields.filter((f) => f.section !== "nursys").map((f) => {
              const common = {
                required: f.required,
                value: values[f.name] || "",
                onChange: (e) => setValues({ ...values, [f.name]: e.target.value }),
                "data-testid": `intake-field-${f.name}`,
              };
              const span = f.type === "textarea" ? "md:col-span-2" : "";
              return (
                <div key={f.name} className={span}>
                  <label className="block text-xs font-semibold text-[#0B1B2B] mb-1.5 uppercase tracking-wide">
                    {f.label}
                    {f.required && <span className="text-[#D4AF37]"> *</span>}
                  </label>
                  {f.type === "select" ? (
                    <select className={inputCls} {...common}>
                      <option value="">Select…</option>
                      {f.options.map((o) => (
                        <option key={o} value={o}>{o}</option>
                      ))}
                    </select>
                  ) : f.type === "textarea" ? (
                    <textarea className={`${inputCls} min-h-[100px]`} {...common} />
                  ) : (
                    <input className={inputCls} type={f.type} {...common} />
                  )}
                </div>
              );
            })}
            {fields.some((f) => f.section === "nursys") && (
              <div className="md:col-span-2 mt-2 pt-6 border-t border-[#E2E8F0]" data-testid="nursys-step">
                <p className="text-xs tracking-[0.2em] uppercase text-[#D4AF37] font-semibold">Final Step · License Verification</p>
                <p className="mt-2 text-xs text-[#64748B] leading-relaxed" data-testid="nursys-consent-note">
                  Why we ask: the details below are required to verify your nursing license and enroll you in the
                  Nursys e-Notify® license-monitoring system operated by the National Council of State Boards of
                  Nursing. They are used only for license verification and monitoring, stored encrypted, and never
                  used for any other purpose.
                </p>
                <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4">
                  {fields.filter((f) => f.section === "nursys").map((f) => {
                    const common = {
                      required: f.required,
                      value: values[f.name] || "",
                      onChange: (e) => setValues({ ...values, [f.name]: e.target.value }),
                      "data-testid": `intake-field-${f.name}`,
                    };
                    return (
                      <div key={f.name}>
                        <label className="block text-xs font-semibold text-[#0B1B2B] mb-1.5 uppercase tracking-wide">
                          {f.label}
                          {f.required && <span className="text-[#D4AF37]"> *</span>}
                        </label>
                        {f.type === "select" ? (
                          <select className={inputCls} {...common}>
                            <option value="">Select…</option>
                            {f.options.map((o) => (
                              <option key={o} value={o}>{o}</option>
                            ))}
                          </select>
                        ) : (
                          <input className={inputCls} type={f.type} {...common} />
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
            {allowUpload && (
              <div className="md:col-span-2">
                <label className="block text-xs font-semibold text-[#0B1B2B] mb-1.5 uppercase tracking-wide">{schema?.upload_label || "CV / License Upload (PDF, DOC — max 10MB)"}</label>
                <label className="flex items-center gap-3 px-4 py-4 rounded-xl border border-dashed border-[#D4AF37]/60 bg-[#F8FAFC] cursor-pointer hover:bg-[#D4AF37]/5 transition-colors">
                  <Paperclip size={18} className="text-[#D4AF37]" />
                  <span className="text-sm text-[#64748B]">
                    {file ? (
                      <span className="inline-flex items-center gap-2">
                        <span className="px-3 py-1 rounded-full bg-[#0B1B2B] text-[#D4AF37] text-xs font-semibold" data-testid="file-badge">{file.name}</span>
                        attached
                      </span>
                    ) : (
                      "Attach credential document"
                    )}
                  </span>
                  <input type="file" className="hidden" accept=".pdf,.doc,.docx,.png,.jpg" onChange={(e) => setFile(e.target.files[0] || null)} data-testid="intake-file-input" />
                </label>
                {file && (
                  <div className="mt-3 grid grid-cols-1 md:grid-cols-3 gap-3 p-4 rounded-xl bg-[#F8FAFC] border border-[#E2E8F0]" data-testid="doc-metadata-panel">
                    <div>
                      <label className="block text-[10px] font-semibold text-[#64748B] mb-1 uppercase tracking-wide">Document Type</label>
                      <select className={inputCls} value={docMeta.document_type} onChange={(e) => setDocMeta({ ...docMeta, document_type: e.target.value })} data-testid="doc-meta-type">
                        <option value="">Select…</option>
                        {["CV / Resume", "License", "Certification", "Transcript", "Agreement", "Other"].map((t) => (
                          <option key={t} value={t}>{t}</option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="block text-[10px] font-semibold text-[#64748B] mb-1 uppercase tracking-wide">Description (optional)</label>
                      <input className={inputCls} maxLength={300} value={docMeta.description} onChange={(e) => setDocMeta({ ...docMeta, description: e.target.value })} data-testid="doc-meta-description" />
                    </div>
                    <div>
                      <label className="block text-[10px] font-semibold text-[#64748B] mb-1 uppercase tracking-wide">Expiration Date (optional)</label>
                      <input className={inputCls} type="date" value={docMeta.expires_on} onChange={(e) => setDocMeta({ ...docMeta, expires_on: e.target.value })} data-testid="doc-meta-expires" />
                    </div>
                  </div>
                )}
              </div>
            )}
            {error && <p className="md:col-span-2 text-sm text-red-600" data-testid="intake-error">{error}</p>}
            <label className="md:col-span-2 flex items-start gap-3 p-4 rounded-xl bg-[#F8FAFC] border border-[#E2E8F0] cursor-pointer">
              <input
                type="checkbox"
                required
                checked={consent}
                onChange={(e) => setConsent(e.target.checked)}
                className="mt-0.5 h-4 w-4 accent-[#D4AF37]"
                data-testid="intake-consent-checkbox"
              />
              <span className="text-xs text-[#64748B] leading-relaxed">
                I consent to the secure processing of the information provided for the purpose of executive review and
                engagement. All materials are encrypted in transit and at rest, access is audit-logged, and data is
                handled in alignment with applicable healthcare privacy and security standards.
              </span>
            </label>
            <div className="md:col-span-2 flex items-center justify-between flex-wrap gap-4 pt-2">
              <div className="flex items-center gap-2 text-xs text-[#64748B]">
                <ShieldCheck size={15} className="text-[#D4AF37]" />
                Encrypted transmission · Executive review within 1–2 business days
              </div>
              <button
                type="submit"
                disabled={busy}
                className="bg-[#D4AF37] text-[#0B1B2B] font-semibold px-8 py-3.5 rounded-xl hover:bg-[#e8c968] hover:shadow-lg transition-all duration-300 disabled:opacity-60 flex items-center gap-2"
                data-testid="intake-submit-button"
              >
                {busy && <Loader2 size={16} className="animate-spin" />}
                Submit Securely
              </button>
            </div>
          </form>
        </div>
      )}
    </Modal>
  );
};
